import { useMutation, useQueryClient } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { extractApiError } from '@/hooks/mutations/utils';
import type { Customer } from '@/hooks/queries/customer';
import { Logger } from '@/utils/logging';

import type {
  UpdateCustomerProfileRequest,
  UpdateCustomerProfileResponse,
  UseUpdateCustomerProfileMutationOptions,
} from './types';

const updateCustomerProfile = async (
  requestData: UpdateCustomerProfileRequest,
): Promise<UpdateCustomerProfileResponse> => {
  try {
    const {
      customerId,
      phone,
      firstName,
      lastName,
      email,
      preferredLanguage,
      birthDate,
    } = requestData;

    const { data } = await apiClient.put<{ customer: Customer }>(
      `/v1/customer/${customerId}`,
      {
        phone: phone.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        preferredLanguage: preferredLanguage.trim(),
        ...(birthDate && { birthDate }),
      },
    );

    return data;
  } catch (error: unknown) {
    Logger.error('Error syncing customer profile', { error });
    throw extractApiError(error, 'UPDATE_PROFILE_ERROR');
  }
};

export const useUpdateCustomerProfileMutation = (
  options?: UseUpdateCustomerProfileMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateCustomerProfile'],
    mutationFn: updateCustomerProfile,
    onError: (err) => {
      Logger.error('Failed to sync customer profile', { error: err });
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['customer', variables.customerId],
      });
    },
    ...options,
  });
};
